import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  getGetDealShortlistQueryKey,
  useUpdateDealShortlistItem,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type NotesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  lpName: string;
  dealId: string;
  shortlistItemId: string;
  canEdit: boolean;
};

export function NotesModal({
  isOpen,
  onClose,
  notes,
  lpName,
  dealId,
  shortlistItemId,
  canEdit,
}: NotesModalProps) {
  const [editedNotes, setEditedNotes] = useState(notes);
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: updateNotes, isPending } = useUpdateDealShortlistItem({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.status_success(),
          description: m.coInvestments_notesUpdated(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
        onClose();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_updateNotes(),
        });
      },
    },
  });

  const handleSave = () => {
    updateNotes({
      dealId,
      dealShortlistItemId: shortlistItemId,
      data: {
        notes: editedNotes,
      },
    });
  };

  const handleClose = () => {
    setEditedNotes(notes);
    onClose();
  };

  return (
    <ActionaryModal
      dataTestId="notes-modal"
      defaultWidth
      fluidHeight
      open={isOpen}
      setOpen={handleClose}
      title={lpName}
    >
      <div className="space-y-4">
        <TextareaInput
          dataTestId="notes-textarea"
          disabled={!canEdit}
          name="notes"
          onChange={(e) => setEditedNotes(e.target.value)}
          placeholder={m.coInvestments_notesPlaceholder()}
          rows={6}
          value={editedNotes}
        />
        <div className="flex gap-2 justify-end">
          {canEdit ? (
            <>
              <ThemedButton
                dataTestId="notes-cancel-button"
                disabled={isPending}
                onClick={handleClose}
                size="small"
                variant="secondary"
              >
                {m.btn_cancel()}
              </ThemedButton>
              <ThemedButton
                dataTestId="notes-save-button"
                disabled={isPending}
                onClick={handleSave}
                size="small"
                variant="primary"
              >
                {isPending ? m.common_pending() : m.btn_save()}
              </ThemedButton>
            </>
          ) : null}
        </div>
      </div>
    </ActionaryModal>
  );
}
